import React from "react";
import { CloseButton } from "../../components/common/MainButtonComponent";

const formatTime = (t) => {
	if (!t) return "";
	try {
		const d = new Date(t);
		if (isNaN(d.getTime())) return String(t);
		return d.toLocaleString();
	} catch {
		return String(t);
	}
};

const getCommentText = (...values) => {
	for (const value of values) {
		if (value === null || value === undefined) {
			continue;
		}

		const normalizedValue = String(value).trim();
		if (normalizedValue) {
			return normalizedValue;
		}
	}

	return "";
};

export default function PaperCommentsDrawer({
	open,
	mode = "view",
	question = null,
	comments = [],
	allComments = [],
	draftComment = "",
	commentError = "",
	commentSubmitting = false,
	onClose,
	onDraftCommentChange,
	onSubmitComment,
}) {
 
	console.log("PaperCommentsDrawer props:", question, comments, allComments);

	const rawComments = Array.isArray(allComments) && allComments.length > 0
		? allComments
		: comments;

	const questionId = question?.question_id ?? question?.assessment_question_id ?? question?.id;
	const visibleComments = mode === "add" && questionId
		? rawComments.filter((comment) => String(comment?.question_id ?? "") === String(questionId))
		: rawComments;

	const enrichedComments = visibleComments.filter((comment) =>
		getCommentText(comment?.comment, comment?.text, comment?.content)
	);

	// Helper to compute a stable grouping key for a comment's question
	const commentQuestionKey = (c) =>
		String(
			c?.question_id ?? c?.assessment_question_id ?? c?.questionNumber ?? c?.question_number ?? "unknown"
		);


	const groupedComments = enrichedComments.reduce((acc, comment) => {
		const key = commentQuestionKey(comment);
		if (!acc[key]) acc[key] = [];
		acc[key].push(comment);
		return acc;
	}, {});

	const renderComment = (comment, index) => (
		<article 
			className="paper-comment" 
			key={comment.id || comment.comment_id || `comment-${index}`}
		>
			{/* Question Context */}
			{comment.question_number || comment.questionNumber ? (
				<div style={{ 
					fontSize: "13px", 
					color: "#4b5563",
					marginBottom: "10px",
					fontWeight: "600",
					letterSpacing: "-0.2px"
				}}>
					<span style={{ display: "block", marginBottom: "2px" }}>Question {comment.question_number || comment.questionNumber}</span>
					{comment.question_title && (
						<span style={{ fontSize: "12px", color: "#7e8996", fontWeight: "500" }}>
							{comment.question_title}
						</span>
					)}
				</div>
			) : null}

			{/* Reviewer/Commenter Info */}
			<div style={{ display: "flex", alignItems: "flex-start", marginBottom: "12px", gap: "10px" }}>
				{comment.reviewer_avatar || comment.avatar ? (
					<img 
						src={comment.reviewer_avatar || comment.avatar} 
						alt={comment.reviewer_name || comment.author || "avatar"}
						style={{
							width: "36px",
							height: "36px",
							borderRadius: "50%",
							objectFit: "cover",
							flexShrink: 0
						}}
					/>
				) : (
					<div 
						style={{
							width: "36px",
							height: "36px",
							borderRadius: "50%",
							backgroundColor: "#007bff",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: "white",
							fontSize: "15px",
							fontWeight: "600",
							flexShrink: 0,
							boxShadow: "0 2px 4px rgba(0, 107, 255, 0.2)"
						}}
					>
						{(comment.reviewer_name || comment.author || "?")[0]?.toUpperCase()}
					</div>
				)}
				<div style={{ flex: 1, minWidth: 0 }}>
					<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px", flexWrap: "wrap" }}>
						<strong style={{ fontSize: "13px", fontWeight: "600", color: "#1f2937" }}>
							{comment.reviewer_name || comment.created_by_name || comment.author || "Unknown"}
						</strong>
						{comment.reviewer_handle && (
							<span style={{ color: "#9ca3af", fontSize: "12px" }}>@{comment.reviewer_handle}</span>
						)}
					</div>
					<div style={{ fontSize: "12px", color: "#9ca3af", fontWeight: "500" }}>
						{formatTime(comment.reviewer_created_at || comment.created_at || comment.time)}
					</div>
				</div>
			</div>

			{/* Comment Content */}
			<p style={{ 
				margin: "10px 0",
				lineHeight: "1.6",
				color: "#374151",
				fontSize: "13px",
				wordBreak: "break-word"
			}}>
				{getCommentText(comment.comment, comment.text, comment.content) ? (
					getCommentText(comment.comment, comment.text, comment.content)
				) : (
					<em style={{ color: "#9ca3af" }}>No comment provided</em>
				)}
			</p>

			{/* Additional Details */}
			{(comment.review_status || comment.mark_as_reviewed) ? (
				<div style={{ 
					fontSize: "12px", 
					color: "#059669",
					marginTop: "10px",
					fontWeight: "600",
					display: "flex",
					alignItems: "center",
					gap: "6px"
				}}>
					<span>✓</span>
					<span>Marked as Reviewed</span>
				</div>
			) : null}
		</article>
	);

	return (
		<>
			{open && (
				<button
					type="button"
					className="paper-comments-backdrop"
					onClick={onClose}
					aria-label="Close comments"
				/>
			)}
			<aside
				className={`paper-comments-drawer ${open ? "open" : ""}`}
				aria-hidden={!open}
			>
				<div className="paper-comments-drawer__header">
					<div>
						<span>{mode === "add" ? "Add Comment" : "Paper Comments"}</span>
						<strong>{enrichedComments.length}</strong>
					</div>
					<CloseButton label="" onClick={onClose} title="Close comments" />
				</div>
				<div className="paper-comments-drawer__body">
					{mode === "add" && question ? (
						<div className="paper-comment" style={{ marginBottom: "16px" }}>
							<div style={{ fontSize: "13px", color: "#4b5563", marginBottom: "8px", fontWeight: 600 }}>
								Question {question.question_number ?? question.currentQIndex ?? ""}
							</div>
							<div style={{ fontSize: "14px", color: "#1f2937", marginBottom: "12px", fontWeight: 600 }}>
								{question.title || question.question_title || "Selected question"}
							</div>
							<textarea
								className="form-control"
								rows={4}
								value={draftComment}
								onChange={(event) => onDraftCommentChange?.(event.target.value)}
								placeholder="Write your comment here"
								disabled={commentSubmitting}
								style={{ resize: "vertical" }}
							/>
							{commentError ? (
								<div className="text-danger mt-2" style={{ fontSize: "12px", fontWeight: 600 }}>
									{commentError}
								</div>
							) : null}
							<div className="d-flex justify-content-end mt-3">
								<button
									type="button"
									className="btn btn-primary"
									disabled={commentSubmitting || !String(draftComment).trim()}
									onClick={onSubmitComment}
								>
									{commentSubmitting ? "Saving..." : "Save Comment"}
								</button>
							</div>
						</div>
					) : null}
					{Object.keys(groupedComments).length ? (
						mode === "add" ? (
							enrichedComments.map((comment, index) => renderComment(comment, index))
						) : (
							Object.entries(groupedComments).map(([qid, comments]) => (
								<div key={qid} className="paper-comment-group" style={{ marginBottom: "16px" }}>
									<div style={{ fontSize: "13px", color: "#4b5563", marginBottom: "8px", fontWeight: 600 }}>
											<span>Question {comments[0]?.question_number || comments[0]?.questionNumber || (qid !== "unknown" ? qid : "")}</span>
											{comments[0]?.question_title && (
												<span style={{ fontSize: "12px", color: "#1f2937", marginLeft: "8px", fontWeight: 600 }}>
													{comments[0].question_title}
												</span>
											)}
										</div>
									{comments.map((comment, idx) => renderComment(comment, idx))}
								</div>
							))
						)
					) : (
						<p className="paper-comments-drawer__empty">
							No comments available.
						</p>
					)}
				</div>
			</aside>
		</>
	);
}

